import { BROKER_SYNC_SECRET_MASK, isRecord } from "@signals/shared";
import { t } from "./broker-sync-panel-format.util";

export function redactKnownSecrets(text: string, secrets: readonly string[]): string {
  let safe = text;
  for (const secret of secrets) {
    if (secret) safe = safe.split(secret).join(BROKER_SYNC_SECRET_MASK);
  }
  return safe.replace(
    /(token|secret|password|authorization|api[_-]?key)(\s*[=:]\s*)[^\s,;]+/gi,
    `$1$2${BROKER_SYNC_SECRET_MASK}`,
  );
}

/**
 * Readable text of a failed API call. `apiFetch` throws the raw response body, so the standard
 * envelope `{ data, error: { message, details } }` is unwrapped here and mapped to Russian for the
 * statuses this panel causes (wrong or missing management code, validation). Raw JSON is never
 * shown and known secrets are masked.
 */
export function describeApiError(error: unknown, secrets: readonly string[]): string {
  const raw = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  let message: string | null = null;
  let details: string[] = [];
  let isEnvelope = false;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isRecord(parsed)) {
      isEnvelope = true;
      const apiError = parsed["error"];
      if (isRecord(apiError) && typeof apiError["message"] === "string") {
        message = apiError["message"];
        const rawDetails = apiError["details"];
        if (Array.isArray(rawDetails)) {
          details = rawDetails.filter((item): item is string => typeof item === "string");
        }
      } else if (typeof apiError === "string") {
        message = apiError;
      } else if (typeof parsed["message"] === "string") {
        message = parsed["message"];
      }
    }
  } catch {
    // Plain-text errors (network failures, timeouts) can still carry a useful message.
  }
  if (message === null && !isEnvelope) {
    const trimmed = raw.trim();
    if (trimmed && !trimmed.startsWith("{") && !trimmed.startsWith("[")) message = trimmed;
  }
  if (!message) return t("portfolio.brokerSync.errorUnknown");
  if (/invalid management code/i.test(message)) return t("portfolio.brokerSync.invalidManagementCode");
  if (/not configured/i.test(message)) return t("portfolio.brokerSync.managementUnavailable");
  if (/validation failed/i.test(message)) {
    return t("portfolio.brokerSync.errorValidation", {
      reason: redactKnownSecrets(details.length > 0 ? details.join("; ") : message, secrets),
    });
  }
  return t("portfolio.brokerSync.errorGeneric", { reason: redactKnownSecrets(message, secrets) });
}

export function sanitizePayload(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sanitizePayload);
  if (!value || typeof value !== "object") return value;
  const result: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value)) {
    if (/(?:token|secret|password|passwd|authorization|cookie|credential|api[_-]?key|private[_-]?key)/i.test(key)) {
      continue;
    }
    result[key] = sanitizePayload(nested);
  }
  return result;
}
